import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, Inject } from '@angular/core';
import { Subscription, interval, takeWhile } from 'rxjs';
import { UserService } from '../../services/user services/user.service';
import { FirestoreService } from '../../services/admin services/firestore.service';
import { Timestamp } from 'firebase/firestore';
import { StorageService } from '../../services/admin services/storage.service';
import { SessionManagementService } from '../../services/admin services/session-management.service';
import { Share } from '@capacitor/share';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AudioVisualizerDirective } from '../../interfaces/audio-visualizer.directive';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AnonymousUserService } from '../../services/user services/anonymous-user.service';


@Component({
  selector: 'app-audio-detail-modal',
  templateUrl: './audio-detail-modal.component.html',
  styleUrls: ['./audio-detail-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AudioVisualizerDirective
  ]
})

export class AudioDetailModalComponent implements OnInit, OnDestroy {




  isPublisher: boolean = false;
  expiresIn: number = 24 * 60 * 60 * 1000;
  audioId: string = '';
  audioUrl: string = '';
  deviceId: string = '';
  timeRemaining: string = '';
  countdown: number = 0;
  likeStatus: 'none' | 'liked' | 'disliked' = 'none';
  isImageLoading: boolean = true;
  likeCount: number = 0;
  dislikeCount: number = 0;
  userId!: string;
  expirationDuration: number = 24 * 60 * 60 * 1000;
  audioTimestamp: Timestamp | null = null;
  priorityAdjustment: number = 0;
  timeAdjustment: number = 0;
  publisheruserId!: string;
  lowQualityUrl: string | null = null;
  highQualityUrl: string | null = null;
  isLowQualityLoaded: boolean = false;
  isHighQualityLoaded: boolean = false;
  hasInteractedOnce: boolean = false;
  private readonly storageKeyPrefix = 'audio_interaction_';

  private countdownSubscription: Subscription | null = null;
  private timerInterval: any = null;

  private timerSubscription?: Subscription;
  private readonly maxaudioDuration: number = 30; // 30 seconds max for reels

  constructor(
    private _anonymousUserService: AnonymousUserService,
    private _firestoreService: FirestoreService,
    private _storageService: StorageService,
    private _sessionManagementService: SessionManagementService,
    public dialogRef: MatDialogRef<AudioDetailModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) { }

  ngOnInit(): void {
    if (this.data) {
      this.audioId = this.data.audioId || '';
      this.audioUrl = this.data.audioUrl || '';
      this.deviceId = this.data.deviceId || '';
      this.isPublisher = this.data.isPublisher || false;
      this.userId = this.data.userId || '';
      this.expiresIn = this.data.expiresIn || (24 * 60 * 60 * 1000);
    }

    // Get current user ID (fallback if not provided)
    if (!this.userId) {
      this.userId = this._anonymousUserService.currentUserId || '';
    }

    this.refreshAudioData();
    this.verifyPublisherStatus();
    this.initializeLikeStatus();
  }

  private async verifyPublisherStatus() {
    // console.log('[audioDetailModal] Verifying publisher status...');

    if (!this.audioId) {
      console.warn('[audioDetailModal] Missing audioId, cannot verify publisher status');
      return;
    }

    try {
      // Get audio data from Firestore
      const audioData = await this._firestoreService.getAudioById(this.audioId);

      // Get device ID from IndexedDB as an additional check
      let idbDeviceId = '';
      try {
        idbDeviceId = await this.getDeviceIdFromIDB();
      } catch (idbError) {
        console.error('[audioDetailModal] Error getting device ID from IndexedDB:', idbError);
        // Continue with the check using the provided deviceId
      }

      // Check all possible matches to determine if user is publisher
      const actualIsPublisher: boolean = !!(
        // Check against provided deviceId
        audioData.userId === this.deviceId ||
        audioData.deviceId === this.deviceId ||
        // Check against IndexedDB deviceId if available
        (idbDeviceId && (audioData.userId === idbDeviceId || audioData.deviceId === idbDeviceId))
      );

      // console.log('[audioDetailModal] Publisher check results:', {
      //   receivedIsPublisher: this.isPublisher,
      //   actualIsPublisher: actualIsPublisher,
      //   audioUserId: audioData.userId,
      //   audioDeviceId: audioData.deviceId,
      //   inputDeviceId: this.deviceId,
      //   idbDeviceId: idbDeviceId
      // });

      // Update the isPublisher flag based on actual data
      if (this.isPublisher !== actualIsPublisher) {
        console.warn(
          '[audioDetailModal] Correcting isPublisher flag!',
          'Received:', this.isPublisher,
          'Actual:', actualIsPublisher
        );
        this.isPublisher = actualIsPublisher;
      }

    } catch (error) {
      console.error('[audioDetailModal] Error verifying publisher status:', error);
    }
  }

  private async getDeviceIdFromIDB(): Promise<string> {
    //console.log('[audioDetailModal] Attempting to get device ID from IndexedDB');
    return new Promise((resolve, reject) => {
      //console.log('[audioDetailModal] Opening user_database');
      const request = indexedDB.open('user_database', 1);

      request.onupgradeneeded = (event) => {
        //  console.log('[audioDetailModal] Database upgrade needed event triggered');
      };

      request.onerror = (event) => {
        console.error('[audioDetailModal] Error opening IndexedDB:', event);
        console.error('[audioDetailModal] Error details:', (event.target as any).error);
        reject('Error opening database');
      };

      request.onsuccess = (event) => {
        // console.log('[audioDetailModal] Database opened successfully');
        const db = request.result;

        // Log available object stores
        // console.log('[audioDetailModal] Available object stores:', Array.from(db.objectStoreNames));

        try {
          // console.log('[audioDetailModal] Creating transaction for user_store');
          const transaction = db.transaction(['user_store'], 'readonly');

          transaction.onerror = (event) => {
            console.error('[audioDetailModal] Transaction error:', event);
            reject('Transaction error');
          };

          //console.log('[audioDetailModal] Getting user_store object store');
          const store = transaction.objectStore('user_store');

          // console.log('[audioDetailModal] Requesting device_id from store');
          // Get the 'device_id' record which contains a key-value pair
          const getRequest = store.get('device_id');

          getRequest.onerror = (event) => {
            console.error('[PhotoDetailModal] Error reading from IndexedDB:', event);
            reject('Error reading from database');
          };

          getRequest.onsuccess = (event) => {
            // console.log('[PhotoDetailModal] Get request completed successfully');
            const result = getRequest.result;
            // console.log('[PhotoDetailModal] Raw result:', result);

            // The IDB format is {key: 'device_id', value: 'ObnoxiousSheep5'}
            if (result && result.value) {
              // console.log('[PhotoDetailModal] Device ID from IndexedDB:', result.value);
              resolve(result.value);
            } else {
              console.warn('[PhotoDetailModal] Device ID not found in IndexedDB');
              resolve('');
            }
          };
        } catch (error) {
          console.error('[PhotoDetailModal] Error in IndexedDB operations:', error);
          reject(`Error in IndexedDB operations: ${error}`);
        }
      };
    });
  }

  async initializeLikeStatus() {
    try {
      // Get current user ID if not already set
      if (!this.userId) {
        this.userId = this._anonymousUserService.currentUserId || '';
      }

      // Fetch the audio data from Firestore
      const audioData = await this._firestoreService.getAudioById(this.audioId);
      if (audioData) {
        const likes = audioData.likes || [];
        const dislikes = audioData.dislikes || [];

        // Set counts
        this.likeCount = likes.length;
        this.dislikeCount = dislikes.length;

        // Set user's status
        if (likes.includes(this.userId)) {
          this.likeStatus = 'liked';
          this.hasInteractedOnce = true;
        } else if (dislikes.includes(this.userId)) {
          this.likeStatus = 'disliked';
          this.hasInteractedOnce = true;
        } else {
          this.likeStatus = 'none';
          // Check local storage for previous interactions
          this.hasInteractedOnce = this.loadInteractionState();
        }

        // Save the current state to local storage if user has interacted
        if (this.hasInteractedOnce) {
          this.saveInteractionState();
        }
      }
    } catch (error) {
      console.error('[PhotoDetailModal] Error initializing like status:', error);
    }
  }

  private loadInteractionState(): boolean {
    const storedState = localStorage.getItem(this.getStorageKey());
    if (storedState && ['liked', 'disliked', 'none'].includes(storedState)) {
      return true;
    }
    return false;
  }

  async likeAudio() {
    try {
      // Skip if user has already interacted once
      if (this.hasInteractedOnce) return;

      // Existing like photo logic
      const previousStatus = this.likeStatus;
      const uid = this._anonymousUserService.currentUserId;
      if (!uid) {
        console.error('[audioDetailModal] User ID not available yet');
        return;
      }

      await this._firestoreService.likeAudio(this.audioId, uid);

      if (previousStatus === 'liked') {
        this.likeCount--;
        this.likeStatus = 'none';
      } else {
        this.likeCount++;
        if (previousStatus === 'disliked') {
          this.dislikeCount--;
        }
        this.likeStatus = 'liked';
      }

      this.hasInteractedOnce = true;
      this.saveInteractionState();

      await this.refreshAudioData();
    } catch (error) {
      console.error('[audioDetailModal] Error liking audio:', error);
    }
  }

  async dislikeAudio() {
    try {
      // Skip if user has already interacted once
      if (this.hasInteractedOnce) return;

      // Existing dislike audio logic
      const previousStatus = this.likeStatus;
      const uid = this._anonymousUserService.currentUserId;
      if (!uid) {
        console.error('[audioDetailModal] User ID not available yet');
        return;
      }

      await this._firestoreService.dislikeAudio(this.audioId, uid);

      if (previousStatus === 'disliked') {
        this.dislikeCount--;
        this.likeStatus = 'none';
      } else {
        this.dislikeCount++;
        if (previousStatus === 'liked') {
          this.likeCount--;
        }
        this.likeStatus = 'disliked';
      }

      // Set interaction flag to true and save to local storage
      this.hasInteractedOnce = true;
      this.saveInteractionState();

      await this.refreshAudioData();
    } catch (error) {
      console.error('[audioDetailModal] Error disliking audio:', error);
    }
  }

  private saveInteractionState(): void {
    if (this.hasInteractedOnce) {
      localStorage.setItem(this.getStorageKey(), this.likeStatus);
    }
  }

  private getStorageKey(): string {
    const uid = this._anonymousUserService.currentUserId;
    return `${this.storageKeyPrefix}${this.audioId}_${uid}`;
  }

  async downloadAudioByThirdParty() {
    try {
      // Basic validation and setup
      if (!this.audioId || !this.audioUrl) {
        this.showToast('Sorry, cannot download this audio.');
        return;
      }

      // Check if we're running in a native environment with Capacitor
      const isNativeEnvironment = typeof (window as any).Capacitor !== 'undefined' &&
        (window as any).Capacitor.isNativePlatform();

      // For native environments, use Capacitor's Filesystem API
      if (isNativeEnvironment) {
        try {
          // Import necessary Capacitor plugins
          const { Filesystem, Directory } = (window as any).Capacitor.Plugins;

          // First, fetch the audio as a blob
          const response = await fetch(this.audioUrl);
          const blob = await response.blob();

          // Convert blob to base64
          const reader = new FileReader();

          // Handle the conversion as a promise
          const base64Data = await new Promise<string>((resolve, reject) => {
            reader.onloadend = () => {
              const base64 = reader.result as string;
              // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
              const base64Data = base64.split(',')[1];
              resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          // Generate a filename with timestamp to prevent overwriting
          const timestamp = new Date().getTime();
          const fileName = `BlinkPic_${this.audioId}_${timestamp}.jpg`;

          // Save the file to device's download/pictures directory
          const result = await Filesystem.writeFile({
            path: fileName,
            data: base64Data,
            directory: Directory.DOCUMENTS,
            recursive: true
          });

          console.log('File saved successfully:', result.uri);
          this.showToast('audio downloaded successfully to your documents folder.');
        } catch (error) {
          console.error('Error downloading audio in native environment:', error);
          this.showToast('Unable to download the audio. Please check app permissions.');
        }
      } else {
        // For browsers, use traditional download approach
        try {
          // Create an anchor element
          const a = document.createElement('a');

          // Set the download attribute and filename
          a.download = `BlinkPic_${this.audioId}.jpg`;

          // If audioUrl is already a blob URL, use it directly
          if (this.audioUrl.startsWith('blob:')) {
            a.href = this.audioUrl;
          }
          // If it's a base64 data URL, use it directly
          else if (this.audioUrl.startsWith('data:')) {
            a.href = this.audioUrl;
          }
          // Otherwise, fetch it first
          else {
            // Fetch the image and create a blob URL
            const response = await fetch(this.audioUrl);
            const blob = await response.blob();
            a.href = URL.createObjectURL(blob);
          }

          // Programmatically click the anchor to trigger the download
          document.body.appendChild(a);
          a.click();

          // Clean up
          document.body.removeChild(a);

          // If we created a blob URL, revoke it to free memory
          if (!this.audioUrl.startsWith('blob:') && !this.audioUrl.startsWith('data:')) {
            URL.revokeObjectURL(a.href);
          }

          console.log('audio downloaded successfully in browser');
          this.showToast('audio downloaded successfully.');
        } catch (error) {
          console.error('Error downloading audio in browser:', error);
          this.showToast('Unable to download the audio. Please try again later.');
        }
      }
    } catch (error) {
      console.error('Unexpected error in downloadaudioByThirdParty:', error);
      if (error instanceof Error) {
        console.error('Error details:', error.message);
      }
      this.showToast('Unable to download the audio. Please try again later.');
    }
  }

  private showToast(message: string): void {
    // Implement your toast logic here, or use the same method you use elsewhere
    // For example:
    // this.toastService.show(message);
  }

  async refreshAudioData() {
    try {
      const audioData = await this._firestoreService.getAudioById(this.audioId);

      if (audioData) {
        // Store audio timestamp and adjustments
        this.audioTimestamp = audioData.timestamp;
        this.timeAdjustment = audioData.timeAdjustment || 0;
        this.publisheruserId = audioData.userId;

        // Start or restart the countdown
        this.startCountdown();
      }
    } catch (error) {
      console.error('[audioDetailModal] Error refreshing audio data:', error);
    }
  }

  startCountdown() {
    // Clear any existing countdown first
    this.clearCountdown();

    this._firestoreService.getAudioById(this.audioId).then((audioData: any) => {
      if (audioData && audioData.timestamp) {
        let timestamp;
        if (audioData.timestamp.toDate) {
          timestamp = audioData.timestamp.toDate();
        } else if (audioData.timestamp.seconds) {
          timestamp = new Date(audioData.timestamp.seconds * 1000);
        } else {
          timestamp = new Date(audioData.timestamp);
        }

        const timeAdjustment = audioData.timeAdjustment || 0;
        const expirationTime = new Date(timestamp.getTime() + this.expiresIn + (timeAdjustment * 1000));
        const currentTime = new Date();
        const secondsRemaining = Math.max(0, Math.floor((expirationTime.getTime() - currentTime.getTime()) / 1000));

        this.countdown = secondsRemaining;
        this.formatTimeRemaining();

        this.countdownSubscription = interval(1000)
          .pipe(takeWhile(() => this.countdown > 0))
          .subscribe({
            next: () => {
              this.countdown--;
              this.formatTimeRemaining();
            },
            complete: () => {
              // Execute audio deletion when the countdown completes
              this.deleteExpiredaudio();
            }
          });
      } else {
        // Fallback to default countdown
        this.countdown = this.expiresIn / 1000;
        this.formatTimeRemaining();

        this.countdownSubscription = interval(1000)
          .pipe(takeWhile(() => this.countdown > 0))
          .subscribe({
            next: () => {
              this.countdown--;
              this.formatTimeRemaining();
            },
            complete: () => {
              // Handle completion
              this.timeRemaining = 'Expired';
            }
          });
      }
    }).catch(error => {
      console.error('[audioDetailModal] Error getting audio timestamp:', error);
      // Fallback countdown
      this.countdown = this.expiresIn / 1000;
      this.formatTimeRemaining();
    });
  }

  formatTimeRemaining() {
    if (this.countdown <= 0) {
      this.timeRemaining = 'Expired';
      return;
    }

    const hours = Math.floor(this.countdown / 3600);
    const minutes = Math.floor((this.countdown % 3600) / 60);
    const seconds = this.countdown % 60;

    if (hours > 0) {
      this.timeRemaining = `${hours}h ${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`;
    } else if (minutes > 0) {
      this.timeRemaining = `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
    } else {
      this.timeRemaining = `${seconds}s`;
    }
  }

  private deleteExpiredaudio() {
    if (this.countdown <= 0) {
      console.log('[audioDetailModal] audio expired, deleting audio:', this.audioId);

      // First get the original audio document
      this._firestoreService.getAudioById(this.audioId)
        .then(audioDoc => {
          // Save the original URL
          const originalUrl = audioDoc?.audioUrl; // Adjust field name if needed

          // Then delete from Firestore
          return this._firestoreService.deleteAudiosFromFirestore(this.audioId)
            .then(() => {
              console.log('[audioDetailModal] audio deleted from Firestore successfully');

              // Only attempt to delete from Storage if we have a valid URL
              if (originalUrl && originalUrl.includes('firebasestorage.googleapis.com')) {
                return this._storageService.deleteAudioFromStorage(originalUrl);
              } else {
                console.log('[audioDetailModal] No Firebase Storage URL found for deletion');
                return Promise.resolve(); // Continue the chain
              }
            });
        })
        .then(() => {
          console.log('[audioDetailModal] audio deleted from Storage successfully');
          this.closeModal();
          this._sessionManagementService.performCleanup('reboot');
        })
        .catch(error => {
          console.error('[audioDetailModal] Error deleting expired audio:', error);
        });
    }
  }

  closeModal() {
    this.dialogRef.close({
      deleted: false,
      liked: this.likeStatus === 'liked' ? true :
        this.likeStatus === 'disliked' ? false : undefined
    });
  }

  private clearCountdown() {
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
      this.countdownSubscription = null;
    }

    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  async deleteAudio() {
    if (confirm('Are you sure you want to delete this audio?')) {
      try {
        console.log('[audioDetailModal] Starting audio deletion process');

        // First get the original audio document to retrieve the Firebase Storage URL
        const audioDoc = await this._firestoreService.getAudioById(this.audioId);
        const originalUrl = audioDoc?.audioUrl;

        // Delete from Firestore
        await this._firestoreService.deleteAudiosFromFirestore(this.audioId);
        console.log('[audioDetailModal] Successfully deleted from Firestore');

        // Try deleting from Storage using the original URL from the database
        if (originalUrl && originalUrl.includes('firebasestorage.googleapis.com')) {
          await this._storageService.deleteAudioFromStorage(originalUrl);
          console.log('[audioDetailModal] Successfully deleted from Storage using original URL');
        }
        // Fallback to the current URL if available
        else if (this.audioUrl && this.audioUrl.includes('firebasestorage.googleapis.com')) {
          await this._storageService.deleteAudioFromStorage(this.audioUrl);
          console.log('[audioDetailModal] Successfully deleted from Storage using current URL');
        }
        else {
          console.log('[audioDetailModal] No Firebase Storage URL found for deletion');
        }

        // Close the dialog and pass result back
        await new Promise(resolve => setTimeout(resolve, 100));
        this.dialogRef.close({
          deleted: true,
          audioId: this.audioId
        });
        console.log('[audioDetailModal] Dialog closed successfully');

      } catch (error) {
        console.error('[audioDetailModal] Error in delete process:', error);
        try {
          this.dialogRef.close({
            deleted: true,
            audioId: this.audioId
          });
        } catch (closeError) {
          console.error('[audioDetailModal] Failed to close dialog:', closeError);
        }
      }
    }
  }


  async shareAudio() {
    try {
      // Check if Web Share API is available (most mobile browsers and some desktop)
      if (navigator.share) {
        await navigator.share({
          title: `Audio by ${this.publisheruserId}`,
          text: 'Check out this audio on BlinkPic!',
          url: window.location.href // Current URL or you could use a shareable URL
        });
        console.log('[audioDetailModal] Audio shared successfully via Web Share API');
      }
      // For Capacitor Share plugin (native mobile)
      else if (typeof Share !== 'undefined') {
        await Share.share({
          title: `Audio by ${this.publisheruserId}`,
          text: 'Check out this audio on BlinkPic!',
          url: window.location.href,
          dialogTitle: 'Share this audio'
        });
        console.log('[audioDetailModal] Audio shared successfully via Capacitor Share');
      }
      // Fallback for browsers without Web Share API
      else {
        console.log('[audioDetailModal] Web Share API not available, showing alternative sharing options');

        // Create a temporary input to copy the URL
        const input = document.createElement('input');
        input.value = window.location.href;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);

        // Show a toast or alert that the URL has been copied
        this.showToast('Link copied to clipboard! You can now paste it anywhere to share.');
      }
    } catch (error) {
      console.error('[audioDetailModal] Error sharing audio:', error);
      this.showToast('Unable to share the audio. Please try again later.');
    }
  }

  ngOnDestroy() {
    // Clean up subscriptions
    this.clearCountdown();
  }
}